<script setup lang="ts">
  import { useGetAgreements } from "@/api/account/account.ts"
  import { QuotesResponse } from "@/api/model"
  import { useCreateQuotesForRenewal } from "@/api/renewals/renewals.ts"
  import { useGetAccountId } from "@/lib/useGetAccountId.ts"
  import { useStepWizard as defaultUseStepWizard } from "@/lib/useStepWizard"
  import { formatCurrency } from "@/lib/utils.ts"
  import ErrorReporter from "@/services/ErrorReporter.ts"
  import { computed, inject, watchEffect } from "vue"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  import TariffCard from "@/views/innovation-tariff/TariffCard.vue"

  const useStepWizard = inject("useStepWizard", defaultUseStepWizard)

  const { goNext } = useStepWizard()

  const { accountId } = useGetAccountId()
  const getAgreementsQuery = useGetAgreements(accountId)
  const agreements = computed(() => getAgreementsQuery.data.value ?? [])
  const currentCostPerMonthInPounds = computed(() =>
    agreements.value.reduce(
      (total, agreement) => total + (agreement.costPerMonthInPounds || 0),
      0,
    ),
  )
  const currentCostPerYearInPounds = computed(() =>
    agreements.value.reduce(
      (total, agreement) => total + (agreement.costPerYearInPounds || 0),
      0,
    ),
  )
  const createQuotesQuery = useCreateQuotesForRenewal()

  watchEffect(async () => {
    if (!accountId.value) return
    try {
      await createQuotesQuery.mutateAsync({
        accountId: accountId.value,
        data: {
          generateQuotesForEvTariffs: false,
          generateQuotesForInnovationTariff: true,
        },
      })
    } catch (error) {
      ErrorReporter.report(error)
      return
    }
  })

  const onSubmit = () => {
    goNext()
  }

  const INNOVATION_TARIFF_PRODUCT_CODE = "E3R-TOU"

  const isInnovationTariffQuote = (quote: QuotesResponse) =>
    quote.elecProductCode !== null &&
    quote.elecProductCode?.startsWith(INNOVATION_TARIFF_PRODUCT_CODE)

  const innovationTariffQuote = computed(() =>
    createQuotesQuery.data.value?.find(isInnovationTariffQuote),
  )
</script>

<template>
  <div
    v-if="agreements && agreements.length > 0"
    class="flex flex-col items-start justify-center gap-4 p-6"
  >
    <h3 class="self-stretch">Your Current Tariff</h3>
    <div v-for="agreement in agreements" :key="agreement.displayName">
      <p>{{ agreement.displayName }}</p>
    </div>
    <div v-if="currentCostPerMonthInPounds && currentCostPerYearInPounds">
      <p class="flex items-baseline">
        <span class="h3">{{
          formatCurrency(currentCostPerMonthInPounds)
        }}</span>
        <span class="body-text ml-2">monthly cost estimated</span>
      </p>
      <p class="flex items-baseline">
        <span class="h3">{{ formatCurrency(currentCostPerYearInPounds) }}</span>
        <span class="body-text ml-2">annual cost estimated</span>
      </p>
    </div>
  </div>
  <form novalidate class="flex flex-col gap-4" @submit.prevent="onSubmit">
    <StepWizardProgressCard title="Choose a plan" :show-next-button="false">
      <div class="flex flex-col items-start justify-center gap-4 p-6">
        <p class="self-stretch">
          Discover a smarter way to manage your energy with our AI-powered
          custom tariff. We analyse your smart meter data to create a
          personalised plan based on when you use the most energy, helping you
          use energy more efficiently and save up to £50 a year, all tailored to
          your lifestyle.
        </p>
      </div>
    </StepWizardProgressCard>
    <TariffCard
      v-if="innovationTariffQuote"
      card-title="Innovation Tariff"
      :monthly-estimate="innovationTariffQuote.costPerMonthInPounds"
      :annual-estimate="innovationTariffQuote.costPerYearInPounds"
      :select-tariff="onSubmit"
    />
  </form>
</template>
